

export interface ValidacaoResultado {
  tipo: 'erro' | 'alerta' | 'sucesso';
  mensagem: string;
  detalhes?: string;
}

export function validarCoeficienteVariacao(cv: number): ValidacaoResultado {
  if (cv === 0) {
    return {
      tipo: 'alerta',
      mensagem: 'Coeficiente de Variação não calculado',
      detalhes: 'Aguardando dados de amostras válidas',
    };
  }

  if (cv <= 10) {
    return {
      tipo: 'sucesso',
      mensagem: 'Excelente precisão',
      detalhes: `CV = ${cv.toFixed(2).replace('.', ',')}% (muito abaixo do limite de 30%)`,
    };
  }

  if (cv <= 20) {
    return {
      tipo: 'sucesso',
      mensagem: 'Boa precisão',
      detalhes: `CV = ${cv.toFixed(2).replace('.', ',')}% (abaixo do limite de 30%)`,
    };
  }

  if (cv <= 30) {
    return {
      tipo: 'sucesso',
      mensagem: 'Precisão adequada para Grau III',
      detalhes: `CV = ${cv.toFixed(2).replace('.', ',')}% (dentro do limite de 30%)`,
    };
  }

  if (cv <= 40) {
    return {
      tipo: 'alerta',
      mensagem: 'CV acima do recomendado',
      detalhes: `CV = ${cv.toFixed(2).replace('.', ',')}% (acima de 30%). Considere adicionar mais amostras ou revisar fatores de homogeneização.`,
    };
  }

  return {
    tipo: 'erro',
    mensagem: 'CV muito alto - precisão insuficiente',
    detalhes: `CV = ${cv.toFixed(2).replace('.', ',')}% (muito acima de 30%). A avaliação não atende os critérios da NBR 14.653-1 para Grau III.`,
  };
}

export function validarNumeroAmostras(n: number, grauDesejado: 'I' | 'II' | 'III' = 'III'): ValidacaoResultado {
  if (n === 0) {
    return {
      tipo: 'erro',
      mensagem: 'Nenhuma amostra válida',
      detalhes: 'É necessário pelo menos 5 amostras para realizar avaliação estatística.',
    };
  }

  if (n < 5) {
    return {
      tipo: 'erro',
      mensagem: 'Número insuficiente de amostras',
      detalhes: `Apenas ${n} amostra(s). Mínimo necessário: 5 amostras.`,
    };
  }

  if (n < 6) {
    return {
      tipo: 'sucesso',
      mensagem: 'Número mínimo de amostras atingido',
      detalhes: `${n} amostras. Recomendado: 6 ou mais para maior confiabilidade estatística.`,
    };
  }

  return {
    tipo: 'sucesso',
    mensagem: 'Quantidade adequada de amostras',
    detalhes: `${n} amostras (acima do mínimo recomendado).`,
  };
}

export function validarIntervaloConfiabilidade(ic: number): ValidacaoResultado {
  if (ic === 0) {
    return {
      tipo: 'alerta',
      mensagem: 'Intervalo de Confiabilidade não calculado',
      detalhes: 'Aguardando dados válidos',
    };
  }

  if (ic <= 20) {
    return {
      tipo: 'sucesso',
      mensagem: 'Grau I de Precisão',
      detalhes: `IC = ${ic.toFixed(2).replace('.', ',')}% (≤ 20%)`,
    };
  }

  if (ic <= 30) {
    return {
      tipo: 'sucesso',
      mensagem: 'Grau II de Precisão',
      detalhes: `IC = ${ic.toFixed(2).replace('.', ',')}% (≤ 30%)`,
    };
  }

  if (ic <= 40) {
    return {
      tipo: 'sucesso',
      mensagem: 'Grau III de Precisão',
      detalhes: `IC = ${ic.toFixed(2).replace('.', ',')}% (≤ 40%)`,
    };
  }

  return {
    tipo: 'erro',
    mensagem: 'IC acima dos limites NBR',
    detalhes: `IC = ${ic.toFixed(2).replace('.', ',')}% (acima de 40%). Não atende aos critérios de precisão da NBR 14.653-1.`,
  };
}

export function validarIdadeVidaUtil(idadeAparente: number, vidaUtil: number): ValidacaoResultado | null {
  if (vidaUtil === 0) {
    return null;
  }

  if (idadeAparente === 0) {
    return null;
  }

  const percentualVida = (idadeAparente / vidaUtil) * 100;

  if (idadeAparente > vidaUtil) {
    return {
      tipo: 'erro',
      mensagem: 'Idade aparente excede vida útil',
      detalhes: `Idade: ${idadeAparente} anos, Vida útil: ${vidaUtil} anos. Verifique os dados de entrada.`,
    };
  }

  if (percentualVida > 90) {
    return {
      tipo: 'alerta',
      mensagem: 'Imóvel próximo do fim da vida útil',
      detalhes: `${percentualVida.toFixed(1).replace('.', ',')}% da vida útil consumida (${idadeAparente}/${vidaUtil} anos).`,
    };
  }

  if (percentualVida > 70) {
    return {
      tipo: 'alerta',
      mensagem: 'Imóvel com idade avançada',
      detalhes: `${percentualVida.toFixed(1).replace('.', ',')}% da vida útil consumida.`,
    };
  }

  return null;
}

export function validarDepreciacao(depreciacao: number): ValidacaoResultado | null {
  if (depreciacao === 0) {
    return null;
  }

  if (depreciacao < 0) {
    return {
      tipo: 'erro',
      mensagem: 'Depreciação negativa',
      detalhes: 'Valor inválido. Verifique os parâmetros de cálculo.',
    };
  }

  if (depreciacao > 1) {
    return {
      tipo: 'erro',
      mensagem: 'Depreciação maior que 100%',
      detalhes: 'Valor inválido. Depreciação não pode exceder 100%.',
    };
  }

  if (depreciacao > 0.80) {
    return {
      tipo: 'alerta',
      mensagem: 'Depreciação muito alta',
      detalhes: `${(depreciacao * 100).toFixed(1).replace('.', ',')}% de depreciação. Verifique se os dados estão corretos.`,
    };
  }

  if (depreciacao > 0.60) {
    return {
      tipo: 'alerta',
      mensagem: 'Depreciação significativa',
      detalhes: `${(depreciacao * 100).toFixed(1).replace('.', ',')}% de depreciação.`,
    };
  }

  return null;
}

export function validarAreaConstruidaVsTerreno(
  areaConstruida: number,
  areaTerreno: number
): ValidacaoResultado | null {
  if (areaConstruida === 0 || areaTerreno === 0) {
    return null;
  }

  const coeficienteAproveitamento = areaConstruida / areaTerreno;

  if (areaConstruida > areaTerreno * 4) {
    return {
      tipo: 'erro',
      mensagem: 'Área construída muito maior que terreno',
      detalhes: `C.A. = ${coeficienteAproveitamento.toFixed(2).replace('.', ',')} (área construída: ${areaConstruida.toFixed(2).replace('.', ',')}m², terreno: ${areaTerreno.toFixed(2).replace('.', ',')}m²). Verifique os dados.`,
    };
  }

  if (areaConstruida > areaTerreno * 3) {
    return {
      tipo: 'alerta',
      mensagem: 'Coeficiente de aproveitamento muito alto',
      detalhes: `C.A. = ${coeficienteAproveitamento.toFixed(2).replace('.', ',')}. Verifique se está correto.`,
    };
  }

  return null;
}

export function validarCubAjustado(
  cubAjustado: number,
  cubBase: number
): ValidacaoResultado | null {
  if (cubAjustado === 0 || cubBase === 0) {
    return null;
  }

  const variacao = ((cubAjustado - cubBase) / cubBase) * 100;

  if (cubAjustado > cubBase * 2) {
    return {
      tipo: 'alerta',
      mensagem: 'CUB Ajustado muito acima do CUB Base',
      detalhes: `Variação de +${variacao.toFixed(1).replace('.', ',')}%. Verifique fatores de ajuste e depreciação.`,
    };
  }

  if (cubAjustado < cubBase * 0.1) {
    return {
      tipo: 'alerta',
      mensagem: 'CUB Ajustado muito abaixo do CUB Base',
      detalhes: `Variação de ${variacao.toFixed(1).replace('.', ',')}%. Depreciação ou ajustes podem estar muito altos.`,
    };
  }

  return null;
}

export function validarOutliersRemovidos(
  totalAmostras: number,
  amostrasSaneadas: number
): ValidacaoResultado | null {
  if (totalAmostras === 0 || amostrasSaneadas === 0) {
    return null;
  }

  const percentualRemovido = ((totalAmostras - amostrasSaneadas) / totalAmostras) * 100;

  if (percentualRemovido === 0) {
    return {
      tipo: 'sucesso',
      mensagem: 'Nenhum outlier detectado',
      detalhes: 'Todas as amostras estão dentro do intervalo de confiança.',
    };
  }

  if (percentualRemovido > 50) {
    return {
      tipo: 'erro',
      mensagem: 'Muitos outliers removidos',
      detalhes: `${percentualRemovido.toFixed(0).replace('.', ',')}% das amostras foram removidas. Revise a qualidade dos dados.`,
    };
  }

  if (percentualRemovido > 30) {
    return {
      tipo: 'alerta',
      mensagem: 'Alto percentual de outliers',
      detalhes: `${percentualRemovido.toFixed(0).replace('.', ',')}% das amostras removidas. Considere revisar os fatores de homogeneização.`,
    };
  }

  return {
    tipo: 'sucesso',
    mensagem: `${totalAmostras - amostrasSaneadas} outlier(s) removido(s)`,
    detalhes: `${percentualRemovido.toFixed(0).replace('.', ',')}% das amostras (${totalAmostras - amostrasSaneadas}/${totalAmostras}).`,
  };
}

export function validarFatorAjusteNaoAverbada(fator: number): ValidacaoResultado | null {
  if (fator === 0) {
    return {
      tipo: 'erro',
      mensagem: 'Fator de ajuste zerado',
      detalhes: 'O fator de ajuste deve ser maior que zero.',
    };
  }

  if (fator > 1) {
    return {
      tipo: 'alerta',
      mensagem: 'Fator de ajuste acima de 1,0',
      detalhes: `Fator = ${fator.toFixed(2).replace('.', ',')}. Área não averbada normalmente tem fator inferior a 1,0.`,
    };
  }

  if (fator < 0.5) {
    return {
      tipo: 'alerta',
      mensagem: 'Fator de ajuste muito baixo',
      detalhes: `Fator = ${fator.toFixed(2).replace('.', ',')}. Valores típicos: 0.60 a 0.80.`,
    };
  }

  return null;
}
